import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormControl,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { RequestsService } from '@api/services/requests.service';
import { IconComponent } from '@ui/icon/icon.component';
import { InputComponent } from '@ui/input/input.component';
import { ButtonComponent } from '@ui/button/button.component';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { CustomersService } from '@api/services/customers.service';

type WithdrawalSearchMode = 'address' | 'wmo' | 'bsn';

interface ContactSearchCriteria {
  postal_code?: string;
  house_number?: string;
  wmo_reference?: string;
  bsn?: string;
}

interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  street: string;
  housenumber: string;
  housenumber_addon?: string;
  postal_code: string;
  city: string;
  date_of_birth?: string;
  bsn?: string;
  phone?: string;
  email?: string;
  wmo_reference?: string;
}

@Component({
  selector: 'app-request-withdrawal-search-step',
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    ButtonComponent,
  ],
  template: `
    <div class="space-y-6">
      <div class="space-y-4">
        <h2 class="text-lg font-medium flex items-center gap-2">
          <ui-icon name="document-text" class="w-5 h-5"></ui-icon>
          Zoek een bestaande client
        </h2>
        <label class="text-sm text-gray-500">
          Zoek op postcode en huisnummer, een WMO-referentie of een BSN. Kies daarna een bestaande client of maak een nieuwe aan.
        </label>

        <div class="grid grid-cols-3 gap-3" role="radiogroup" aria-label="Zoek op">
          @for(mode of searchModes; track mode.value) {
          <button
            type="button"
            role="radio"
            [attr.aria-checked]="searchMode() === mode.value"
            (click)="setSearchMode(mode.value)"
            class="p-3 border rounded-lg text-left text-sm font-medium hover:border-primary-500"
            [class.border-primary-500]="searchMode() === mode.value"
            [class.bg-primary-50]="searchMode() === mode.value"
            [class.border-gray-300]="searchMode() !== mode.value"
          >
            {{ mode.label }}
          </button>
          }
        </div>

        @if(searchMode() === 'address') {
        <div class="grid grid-cols-3 gap-4">
          <ui-input
            label="Postcode"
            [formControl]="postalCodeControl"
            [required]="true"
            placeholder="1234AB"
            [error]="getPostalCodeErrorMessage()"
          ></ui-input>
          <ui-input
            label="Huisnummer"
            [formControl]="houseNumberControl"
            [required]="true"
            placeholder="123"
            [error]="getHouseNumberErrorMessage()"
          ></ui-input>
          <ui-input
            label="Huisnummer toevoeging"
            [formControl]="houseNumberAddonControl"
            placeholder="B"
          ></ui-input>
        </div>
        } @else if(searchMode() === 'wmo') {
        <div class="grid grid-cols-2 gap-4">
          <ui-input
            label="WMO-referentie"
            [formControl]="searchWmoReferenceControl"
            [required]="true"
            placeholder="WMO-referentie"
            [error]="getSearchWmoReferenceErrorMessage()"
          ></ui-input>
        </div>
        } @else {
        <div class="grid grid-cols-2 gap-4">
          <ui-input
            label="BSN"
            [formControl]="bsnControl"
            [required]="true"
            placeholder="123456789"
            [error]="getBsnErrorMessage()"
          ></ui-input>
        </div>
        }

        @if(isSearching()) {
        <div class="flex items-center justify-center py-4">
          <ui-icon name="spinner" class="animate-spin w-6 h-6"></ui-icon>
        </div>
        } @if(searchResults() && searchResults().length > 0) {
        <div class="mt-4 space-y-4">
          <h3 class="text-sm font-medium text-gray-700">
            Gevonden resultaten:
          </h3>
          <div class="space-y-2 text-sm">
            @for(result of searchResults(); track result.id) {
            <div
              class="p-4 border rounded-lg hover:border-primary-500 cursor-pointer"
              [class.border-primary-500]="selectedResult()?.id === result.id"
              [class.bg-primary-50]="selectedResult()?.id === result.id"
              (click)="selectResult(result)"
            >
              <div class="flex items-center gap-2">
                <ui-icon name="circle" class="w-5 h-5 text-gray-500"></ui-icon>
                <span class="font-medium">
                  {{ result.wmo_reference }}
                </span>
                @if(selectedResult()?.id === result.id) {
                <ui-icon
                  name="check"
                  class="w-5 h-5 text-primary-500 ml-auto"
                ></ui-icon>
                }
              </div>
              <div class="flex items-center gap-2 mt-2">
                <ui-icon name="person" class="w-5 h-5 text-gray-500"></ui-icon>
                <span class="font-medium">
                  {{ result.first_name }} {{ result.last_name }}
                </span>
              </div>
              <div class="flex items-center gap-2 mt-2">
                <ui-icon name="map-pin" class="w-5 h-5 text-gray-500"></ui-icon>
                <span>
                  {{ result.street }} {{ result.housenumber }}
                  {{ result.housenumber_addon }}, {{ result.postal_code }}
                  {{ result.city }}
                </span>
              </div>
            </div>
            }
          </div>
        </div>
        } @if(hasSearched() && searchResults().length === 0 && !isSearching())
        {
        <div class="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div class="space-y-2">
            <h4 class="text-sm font-medium text-gray-900">Geen resultaten gevonden</h4>
            <div class="space-y-2 text-sm text-gray-600 leading-relaxed">
              <p>
                Mogelijk is de aanvraag voor deze lift niet via Smienk Connect gedaan. Maak een nieuwe klant aan om verder te gaan met het terugnameverzoek.
              </p>
              <p>
                <span class="font-medium text-gray-700">Let op:</span> het kan zijn dat er wel een opdracht zichtbaar is met klantgegevens, maar dat er geen bijbehorende klant bestaat in Smienk Connect.
              </p>
            </div>
          </div>
        </div>
        } @if(hasSearched() && !isSearching()) {
        <div class="mt-4">
          <div
            class="p-4 border rounded-lg cursor-pointer"
            [class.border-primary-500]="newClient()"
            [class.bg-primary-50]="newClient()"
            [class.border-gray-300]="!newClient()"
            [class.hover:border-primary-500]="!newClient()"
            (click)="selectNewClient()"
          >
            <div class="flex items-center gap-2">
              <ui-icon name="add" class="w-5 h-5 text-gray-500"></ui-icon>
              <span class="font-medium text-gray-700">
                Nieuwe client toevoegen
              </span>
              @if(newClient()) {
              <ui-icon
                name="check"
                class="w-5 h-5 text-primary-500 ml-auto"
              ></ui-icon>
              }
            </div>
            <div class="mt-2 text-sm text-gray-500">
              Voer de gegevens handmatig in voor een nieuwe client
            </div>
          </div>
        </div>
        }
        @if(newClient()) {
        <div class="grid grid-cols-2 gap-4">
          <div class="relative">
            <ui-input
              label="WMO referentie"
              [formControl]="wmoReferenceControl"
              [required]="true"
              placeholder="WMO referentie"
              [error]="getWmoReferenceErrorMessage()"
            ></ui-input>
            @if(isCheckingWmoReference()) {
            <div class="absolute right-2 top-8">
              <ui-icon name="spinner" class="animate-spin"></ui-icon>
            </div>
            }
          </div>
        </div>
        }
      </div>
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestWithdrawalSearchStepComponent {
  private requestsService = inject(RequestsService);
  private customersService = inject(CustomersService);
  private fb = inject(FormBuilder);
  @Input() requestForm!: FormGroup;

  isSearching = signal<boolean>(false);
  searchResults = signal<Contact[]>([]);
  hasSearched = signal<boolean>(false);
  selectedResult = signal<Contact | null>(null);
  newClient = signal<boolean>(false);
  searchMode = signal<WithdrawalSearchMode>('address');
  searchModes: { value: WithdrawalSearchMode; label: string }[] = [
    { value: 'address', label: 'Postcode en huisnummer' },
    { value: 'wmo', label: 'WMO-referentie' },
    { value: 'bsn', label: 'BSN' },
  ];

  getPostalCodeErrorMessage(): string {
    if (this.searchMode() !== 'address') {
      return '';
    }
    const control = this.postalCodeControl;
    if (!control?.touched) return '';

    if (!control.value?.trim()) {
      return 'Postcode is verplicht';
    }
    if (control.errors?.['invalidPostalCode']) {
      return 'Voer een geldige postcode in (1234AB)';
    }
    return '';
  }

  getHouseNumberErrorMessage(): string {
    if (this.searchMode() !== 'address' || !this.houseNumberControl.touched) {
      return '';
    }
    if (!this.houseNumberControl.value?.trim()) {
      return 'Huisnummer is verplicht';
    }
    return '';
  }

  getSearchWmoReferenceErrorMessage(): string {
    if (this.searchMode() !== 'wmo' || !this.searchWmoReferenceControl.touched) {
      return '';
    }
    if (!this.searchWmoReferenceControl.value?.trim()) {
      return 'WMO-referentie is verplicht';
    }
    return '';
  }

  getBsnErrorMessage(): string {
    if (this.searchMode() !== 'bsn' || !this.bsnControl.touched) {
      return '';
    }
    const value = (this.bsnControl.value ?? '').replace(/\s+/g, '');
    if (!value) {
      return 'BSN is verplicht';
    }
    if (this.bsnControl.errors?.['invalidBsn']) {
      return 'BSN moet uit 9 cijfers bestaan';
    }
    return '';
  }

  isCheckingWmoReference = signal<boolean>(false);
  get wmoReferenceControl(): FormControl {
    return this.requestForm
      .get('wmo_reference')
      ?.get('wmo_reference') as FormControl;
  }

  getWmoReferenceErrorMessage(): string {
    const control = this.wmoReferenceControl;
    if (!control?.touched) return '';
    if (control.errors?.['required']) {
      return 'WMO referentie is verplicht';
    }
    if (control.errors?.['invalid']) {
      return control.errors['invalid'];
    }
    return '';
  }

  searchForm = this.fb.group({
    search_postal_code: ['', [this.postalCodeValidator.bind(this)]],
    search_housenumber: [''],
    search_housenumber_addon: [''],
    search_wmo_reference: [''],
    search_bsn: ['', [this.bsnValidator.bind(this)]],
  });

  get postalCodeControl(): FormControl {
    const control = this.searchForm.get('search_postal_code');
    if (!control) {
      throw new Error('Postal code control not found');
    }
    return control as FormControl;
  }

  get houseNumberControl(): FormControl {
    const control = this.searchForm.get('search_housenumber');
    if (!control) {
      throw new Error('House number control not found');
    }
    return control as FormControl;
  }

  get houseNumberAddonControl(): FormControl {
    const control = this.searchForm.get('search_housenumber_addon');
    if (!control) {
      throw new Error('House number addon control not found');
    }
    return control as FormControl;
  }

  get searchWmoReferenceControl(): FormControl {
    const control = this.searchForm.get('search_wmo_reference');
    if (!control) {
      throw new Error('Search WMO reference control not found');
    }
    return control as FormControl;
  }

  get bsnControl(): FormControl {
    const control = this.searchForm.get('search_bsn');
    if (!control) {
      throw new Error('BSN control not found');
    }
    return control as FormControl;
  }

  ngOnInit() {
    this.searchForm.valueChanges
      .pipe(
        debounceTime(500),
        map(() => this.activeSearchCriteria()),
        distinctUntilChanged(
          (previous, current) =>
            JSON.stringify(previous) === JSON.stringify(current)
        ),
        tap((criteria) => {
          this.selectedResult.set(null);
          this.newClient.set(false);
          this.clearSelectedClient();
          if (!criteria) {
            this.isSearching.set(false);
            this.searchResults.set([]);
            this.hasSearched.set(false);
            return;
          }
          this.isSearching.set(true);
          this.searchResults.set([]);
          this.hasSearched.set(true);
        }),
        switchMap((criteria) => {
          if (!criteria) {
            return of(null);
          }
          return this.requestsService
            .searchExistingContacts(criteria)
            .pipe(catchError(() => of([] as Contact[])));
        })
      )
      .subscribe((results) => {
        if (results === null) {
          return;
        }
        this.isSearching.set(false);
        this.searchResults.set(results);
      });

    this.wmoReferenceControl.valueChanges
      .pipe(
        filter(
          (value): value is string =>
            !!value && value.length > 0 && this.newClient()
        ),
        tap(() => {
          this.wmoReferenceControl.setErrors({ invalid: 'Zoeken...' });
        }),
        debounceTime(500),
        tap(() => {
          this.isCheckingWmoReference.set(true);
          this.wmoReferenceControl.setErrors({ invalid: 'Zoeken...' });
        }),
        switchMap((value) => this.requestsService.checkWmoReference(value))
      )
      .subscribe({
        next: (response) => {
          this.isCheckingWmoReference.set(false);
          if (response.exists) {
            let message = response.message;
            this.wmoReferenceControl.setErrors({ invalid: response.message });
          } else {
            this.wmoReferenceControl.setErrors(null);
          }
        },
        error: () => {
          this.isCheckingWmoReference.set(false);
        },
      });
  }

  selectResult(result: Contact) {
    this.selectedResult.set(result);
    this.newClient.set(false);
    // Fill the form with the selected result's data
    this.requestForm.patchValue({
      contact_id: result.id,
      first_name: result.first_name,
      last_name: result.last_name,
      street: result.street,
      housenumber: result.housenumber,
      housenumber_addon: result.housenumber_addon,
      postal_code: result.postal_code,
      city: result.city,
      date_of_birth: result.date_of_birth,
      bsn: result.bsn,
      phone: result.phone,
      email: result.email,
    });
    this.requestForm
      .get('wmo_reference')
      ?.get('wmo_reference')
      ?.setValue(result.wmo_reference);
  }

  setSearchMode(mode: WithdrawalSearchMode): void {
    if (this.searchMode() === mode) {
      return;
    }

    this.searchMode.set(mode);
    this.searchForm.reset();
    this.isSearching.set(false);
    this.searchResults.set([]);
    this.hasSearched.set(false);
    this.selectedResult.set(null);
    this.newClient.set(false);
    this.clearSelectedClient();
  }

  selectNewClient() {
    this.selectedResult.set(null);
    this.newClient.set(true);
    const mode = this.searchMode();
    this.requestForm.patchValue({
      contact_id: '',
      first_name: '',
      last_name: '',
      street: '',
      housenumber: mode === 'address' ? this.houseNumberControl.value : '',
      housenumber_addon:
        mode === 'address' ? this.houseNumberAddonControl.value : '',
      postal_code: mode === 'address' ? this.postalCodeControl.value : '',
      city: '',
      date_of_birth: '',
      bsn:
        mode === 'bsn'
          ? (this.bsnControl.value ?? '').replace(/\s+/g, '')
          : '',
      phone: '',
      email: '',
    });
    this.wmoReferenceControl.setValue(
      mode === 'wmo' ? this.searchWmoReferenceControl.value?.trim() ?? '' : ''
    );
  }

  private clearSelectedClient(): void {
    this.requestForm.patchValue({
      contact_id: '',
      first_name: '',
      last_name: '',
      street: '',
      housenumber: '',
      housenumber_addon: '',
      postal_code: '',
      city: '',
      date_of_birth: '',
      bsn: '',
      phone: '',
      email: '',
    });
    this.wmoReferenceControl.setValue('');
  }

  private activeSearchCriteria(): ContactSearchCriteria | null {
    if (this.searchMode() === 'address') {
      const postalCode = (this.postalCodeControl.value ?? '').trim();
      const houseNumber = (this.houseNumberControl.value ?? '').trim();
      if (!postalCode || !houseNumber || this.postalCodeControl.invalid) {
        return null;
      }

      return {
        postal_code: postalCode,
        house_number: houseNumber,
      };
    }

    if (this.searchMode() === 'wmo') {
      const wmoReference = (this.searchWmoReferenceControl.value ?? '').trim();
      if (!wmoReference) {
        return null;
      }

      return { wmo_reference: wmoReference };
    }

    const bsn = (this.bsnControl.value ?? '').replace(/\s+/g, '');
    if (!/^\d{9}$/.test(bsn)) {
      return null;
    }

    return { bsn };
  }

  private postalCodeValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    // Nederlandse postcode format: 4 cijfers + optionele spatie + 2 hoofdletters (1234 AB of 1234AB)
    const postalCodeRegex = /^[1-9][0-9]{3}\s?[A-Z]{2}$/i;
    const isValid = postalCodeRegex.test(value);

    return isValid ? null : { invalidPostalCode: true };
  }

  private bsnValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const normalizedValue = String(value).replace(/\s+/g, '');
    return /^\d{9}$/.test(normalizedValue) ? null : { invalidBsn: true };
  }
}
